import React, { useEffect, useState } from 'react';
import Wrapper from './Wrapper';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentPage,
  requestsSelector,
  setCurrentRequestFilesSize,
  setNextButtonEnabled,
  setNextButtonToolTip,
  setRequestData,
  setSubmitButtonTitle,
  updateRequestPages,
} from '../../../redux/slicers/requestSlicer';
import Tips from '../../../components/Request/Tips';
import styles from './Notes.module.scss';
import UploadedFiles from '../../../components/Request/UploadedFiles';
import cx from 'classnames';
import { apiDeleteRequestFile, apiGetRequest, apiUpdateRequest } from '../../../helpers/api';
import { checkFileTypes, getFileFormData, setDefaultFiles, uploadRequestFile } from './helper';
import { useUserContext } from '../../../contexts/UserContext';
import { removeExifFromMultipleFiles } from '../../../helpers/jsHelper';
import { REQUEST_SUPPORTED_FILE_FORMATS } from '../../../constants/constants';

function Notes({ isReadOnly }) {
  const { isGuestUser } = useUserContext();
  const _requestSelector = useSelector(requestsSelector);
  const currentPage = useSelector(getCurrentPage);
  const [value, setValue] = useState('');
  const [files, setFiles] = useState([]);
  const dispatch = useDispatch();
  const TIP_TEXT =
    "If we don't have a Cad file to work with, we usually trace the image to create a model. To make this process easier, it really helps if the images are taken at eye level and are head-on to accurately capture proportions.";

  useEffect(async ()=> {
    if(_requestSelector?.currentRequestSubPageId){
      if (_requestSelector.currentRequestId) {
        const final_json_data = { ..._requestSelector };
        const formData = new FormData();
        formData.append('json_data', JSON.stringify(final_json_data));
        await apiUpdateRequest(_requestSelector.currentRequestId, formData);
      }
    }
  }),[_requestSelector?.currentRequestSubPageId]
    
  useEffect(() => {
    if (isReadOnly) return;
    dispatch(setSubmitButtonTitle('Review request'));
    dispatch(
      setRequestData({
        note: '',
        subPages: [{ id: 4, showMainPageAlso: true }],
      })
    );

    dispatch(
      updateRequestPages({
        ...currentPage,
        data: {
          note: '',
        },
        isCompleted: true,
      })
    );

    return () => dispatch(setSubmitButtonTitle('Next'));
  }, []);

  const handleChange = (e) => {
    dispatch(
      setRequestData({
        note: value,
        subPages: [{ id: 4, showMainPageAlso: true }],
      })
    );
    setValue(e.target.value);
  };

  const handleFileUpload = async (e) => {
    let _files = [...e.target.files];
    if (
      !checkFileTypes(
        _files,
        REQUEST_SUPPORTED_FILE_FORMATS,
        'The uploaded file format is not supported'
      )
    ) {
      return;
    }
    _files = await removeExifFromMultipleFiles(_files);
    if (!_files.length) return;

    const formData = getFileFormData(_files, _requestSelector.currentRequestId, 'additionalFiles');
    uploadRequestFile(formData, _files, files, setFiles, null, false, dispatch, _requestSelector);
  };

  const handleDeleteClick = async (_id) => {
    const _file = files.find((item) => item.id === _id);
    dispatch(
      setCurrentRequestFilesSize(_requestSelector.currentRequestFilesSize - _file.file.size)
    );

    const _files = files.filter((item) => item.id != _id);
    setFiles(_files);
    await apiDeleteRequestFile(_id);
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13 && !e.shiftKey) {
      e.target.blur();
    }
  };

  const handleBlur = async () => {
    const formData = new FormData();
    formData.append('additional_notes', value);

    if (isGuestUser()) {
      formData.append('is_internal_team', true);
    }

    await apiUpdateRequest(_requestSelector.currentRequestId, formData);
  };

  useEffect(async () => {
    const response = await apiGetRequest(_requestSelector.currentRequestId);
    if (response) setValue(response.additional_notes);

    setDefaultFiles(_requestSelector.currentRequestId, 'additionalFiles', setFiles);

    if (isReadOnly) return;
  }, []);

  useEffect(() => {
    if (
      _requestSelector.filesUploadStatus &&
      Object.keys(_requestSelector.filesUploadStatus).filter(
        (_item) => !_requestSelector.filesUploadStatus[_item]
      ).length
    ) {
      dispatch(setNextButtonEnabled(false));
      dispatch(
        setNextButtonToolTip(
          'Submit button is disabled because some of your files are being uploaded.'
        )
      );
    } else {
      dispatch(setNextButtonEnabled(true));
      dispatch(setNextButtonToolTip(null));
    }
  }, [_requestSelector.filesUploadStatus]);

  useEffect(() => {
    return () => {
      dispatch(setRequestData(''));
      dispatch(setNextButtonToolTip(null));
    };
  }, []);

  return (
    <Wrapper
      title={!isReadOnly && `Extra informations for ${_requestSelector.requestName}`}
      isReadOnly={isReadOnly}>
      <div className="d-flex gap-3">
        <div className={cx(styles.notesWrapper, isReadOnly && 'w-100 mt-3')}>
          <h6>Additional Notes</h6>

          <>
            <div className={styles.notesBody}>
              <textarea
                name="notes"
                cols="20"
                rows="5"
                placeholder="Notes"
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={isReadOnly}></textarea>
            </div>
            <div
              className={cx('p-3 w-100 d-grid gap-3 mb-4')}
              style={{
                gridTemplateColumns: 'repeat(auto-fill, minmax(215px, 1fr))',
              }}>
              {files.map(
                (file) =>
                  file.file && (
                    <UploadedFiles
                      _file={file}
                      onDeleteClick={() => handleDeleteClick(file.id)}
                      isReadOnly={isReadOnly}
                      variant={2}
                    />
                  )
              )}
            </div>
            {!isReadOnly ? (
              <label
                forHtml="_file"
                className={cx(styles.addFile, files.length && styles.paddingBottom)}>
                <input
                  type="file"
                  id="_file"
                  accept={REQUEST_SUPPORTED_FILE_FORMATS.join(', ')}
                  hidden
                  multiple
                  onChange={handleFileUpload}
                />
                + Add files
              </label>
            ) : null}
          </>
        </div>
        {!isReadOnly && <Tips title="Notes" text={TIP_TEXT} marginTop={0} />}
      </div>
    </Wrapper>
  );
}

export default Notes;
